import React, { useContext, useMemo } from 'react';
import { ISuggestion } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { definedSelectedId } from '../../../../../../../Components/MasterDetail/MasterDetailUtils';
import { TEDLRFakturaAttestAttestTypEnum } from '../../../../../../../globalQueryTypes';
import {
  IColumnDef,
  ICustomValidValues,
} from '../../../../../../../Types/ColumnDefType';
import { ISelectedDetail } from '../../../../../../../Types/MasterDetailTypes';
import { GridContext } from '../../../../../../GridContext';
import { getKontodelCustomConfigs } from '../../../../../Ekonomi/ObjektVyer/Bokforder/Utils/BokforderUtils';
import {
  AssociationList,
  IAssociationListConfig,
} from '../../../../../_Common/Components/BusinessCollectionComponents/AssociationList/AssociationList';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import KonteringContext from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import { IKontodelDef } from '../../../../../_Common/Components/KonteringManager/Query/KonteringManagerQuery';
import { FakturaDelType__Associations_Fakturaattest_All } from '../../Queries/_QueryTypes/FakturaDelType';
import FakturaDelContext from '../FakturaDelContextManager/FakturaDelContext';

const getCustomValidKoddelarOptions = (
  activeKontodelDef: IKontodelDef
): ICustomValidValues => {
  const result: ICustomValidValues = {
    validValuesFunction: (defaultValues, formState, parentFormState) => {
      let result: ISuggestion[] = [];

      result = activeKontodelDef.kontodelar.flatMap(kontodel => {
        const elem: ISuggestion = {
          name: `${kontodel.kontodel}`,
          value: kontodel.id,
          // Måste heta versaler för att uppdatera korrekt värde i changedValues.
          data: { KONTODEL: kontodel.kontodel, BENAMNING: kontodel.benamning },
        };
        return elem;
      });

      return result;
    },
    skipDefaultValues: true,
  };

  return result;
};

const getCustomValidAttestanter = (): ICustomValidValues => {
  const result: ICustomValidValues = {
    validValuesFunction: (defaultValues, formState, parentFormState) => {
      let result: ISuggestion[] = [];
      const allFakturaAttester: FakturaDelType__Associations_Fakturaattest_All[] =
        parentFormState?.currentValues?._Associations?.Fakturaattest_All;

      result = allFakturaAttester
        .filter(fakturaAttest => {
          return (
            fakturaAttest.AttestTyp ===
            TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
          );
        })
        .map(beslutAttestant => {
          const suggestion: ISuggestion = {
            name: beslutAttestant.Attestant ?? 'inget namn hittat',
            value: beslutAttestant.id,
          };
          return suggestion;
        });

      return result;
    },
    skipDefaultValues: true,
    useParentFormState: true,
  };

  return result;
};

const getFakturadelCustomColumns = (
  allKontodelDefs: IKontodelDef[],
  bokfDate: Date
) => {
  const customColumns: IColumnDef[] = [];
  for (let index = 0; index < allKontodelDefs.length; index++) {
    const kontodelDef = allKontodelDefs[index];
    if (
      kontodelDef.visibleFromDate <= bokfDate &&
      kontodelDef.visibleToDate >= bokfDate
    ) {
      const ordningsNr = kontodelDef.ordningsNr;
      const elem: IColumnDef = {
        association: `Kontodel${ordningsNr}`,
        attributeDef: 'KONTODEL',
        caption: kontodelDef.namn,
        customValidValues: getCustomValidKoddelarOptions(kontodelDef),
      };
      customColumns.push(elem);
    }
  }

  customColumns.push({ attributeDef: 'DEBET' });
  // customColumns.push({ attributeDef: 'KREDIT' });

  customColumns.push({
    association: 'Fakturaattest',
    attributeDef: 'Attestant',
    caption: 'Beslutsattestant',
    customValidValues: getCustomValidAttestanter(),
  });

  return customColumns;
};

interface IProps {
  id: string | ISelectedDetail;
}
const FastRegGrid: React.FC<React.PropsWithChildren<IProps>> = props => {
  const konteringContext = useContext(KonteringContext);
  const { getActiveAttestKontodelDefsByKodplanId } =
    konteringContext.konteringUtils;
  const fakturaDelContext = useContext(FakturaDelContext);
  const formContext = useContext(BusinessDataFormContext);

  const konteringGridConfig: IAssociationListConfig = useMemo(() => {
    const kontodelDefFromKodplan = getActiveAttestKontodelDefsByKodplanId(
      fakturaDelContext.kodplanId,
      fakturaDelContext.bokfDatum,
      konteringContext.attestKodbehorigheterUrval ?? [],
      formContext?.formState.currentValues?._Associations?.Faktura
        ?.IngaendeMomsBaskonto,
      fakturaDelContext.isKobFaktura
    );

    return {
      inCard: true,
      integrated: true,
      defaultVisningstyp: 'tabell',
      customDefaultColumns: getFakturadelCustomColumns(
        kontodelDefFromKodplan,
        fakturaDelContext?.bokfDatum
      ),
      // sumColumns: [{ columnName: 'DEBET' }, { columnName: 'KREDIT' }],
      customBusinessConfig: {
        associations: getKontodelCustomConfigs(kontodelDefFromKodplan),
      },
    };
  }, [
    getActiveAttestKontodelDefsByKodplanId,
    fakturaDelContext.bokfDatum,
    fakturaDelContext.isKobFaktura,
    fakturaDelContext.kodplanId,
    konteringContext.attestKodbehorigheterUrval,
    formContext?.formState.currentValues?._Associations?.Faktura
      ?.IngaendeMomsBaskonto,
  ]);

  return (
    <>
      {!formContext?.formState.loading &&
        !konteringContext.konteringValidationQueryLoading && (
          <GridContext.Provider value={{ inGrid: true }}>
            <AssociationList
              associationName="Konteringsforslag"
              associationPath="Faktura.Konteringsforslag"
              collectionName={'LRLeverantorfaktura'}
              ownId={definedSelectedId(props.id)}
              config={konteringGridConfig}
            />
          </GridContext.Provider>
        )}
    </>
  );
};

export default FastRegGrid;
